import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { formatCurrency, formatDate } from '@/lib/utils';
import { Eye, Loader2 } from 'lucide-react';
import type { Order } from '../types';

interface OrderTableProps {
  orders: Order[];
  onViewJourney?: (orderId: number) => void;
  loading?: boolean;
}

export function OrderTable({ orders, onViewJourney, loading = false }: OrderTableProps) {
  return (
    <div className="relative rounded-md border">
      {loading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/70 backdrop-blur-[1px] rounded-md">
          <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
        </div>
      )}
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Order</TableHead>
            <TableHead>Date</TableHead>
            <TableHead>Customer</TableHead>
            <TableHead>Distributor</TableHead>
            <TableHead>Rule Matched</TableHead>
            <TableHead className="text-right">Amount</TableHead>
            <TableHead>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {orders.map((order) => (
            <TableRow key={order.id}>
              <TableCell className="font-medium">
                {order.shopifyOrderNumber?.startsWith('#')
                  ? order.shopifyOrderNumber
                  : `#${order.shopifyOrderNumber}`}
              </TableCell>
              <TableCell className="text-sm text-muted-foreground">
                {formatDate(order.shopifyCreatedAt || order.createdAt)}
              </TableCell>
              <TableCell className="text-sm">
                {order.email || order.customerId || (
                  <span className="text-muted-foreground">—</span>
                )}
              </TableCell>
              <TableCell className="text-sm">
                {order.attributedDistributorId ? (
                  <Badge variant="outline">{order.attributedDistributorId}</Badge>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </TableCell>
              <TableCell className="text-sm">
                {order.rule ? (
                  <div className="flex flex-col gap-0.5">
                    <span className="font-medium">{order.rule.name}</span>
                    <span className="text-xs text-muted-foreground">{order.rule.type}</span>
                  </div>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </TableCell>
              <TableCell className="text-right font-medium">
                {formatCurrency(order.totalPrice, order.currency)}
              </TableCell>
              <TableCell>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onViewJourney?.(order.id)}
                  title="View Journey"
                >
                  <Eye className="h-4 w-4" />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
